import { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Package, CheckCircle, XCircle, Plus, Edit, Archive } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoucherManagement = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [certTypeFilter, setCertTypeFilter] = useState('all');
  const [certTypes, setCertTypes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [formData, setFormData] = useState({ code: '', certification_type: '' });

  useEffect(() => {
    fetchVouchers();
    fetchCertTypes();
  }, [certTypeFilter, showArchived]);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const params = { include_archived: showArchived };
      if (certTypeFilter !== 'all') params.cert_type = certTypeFilter;
      const response = await axios.get(`${API}/vouchers`, { params });
      setVouchers(response.data);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCertTypes = async () => {
    try {
      const response = await axios.get(`${API}/vouchers/types`);
      setCertTypes(response.data.types);
    } catch (error) {
      console.error('Failed to fetch cert types');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/vouchers/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        },
      });
      
      toast.success(`Successfully uploaded ${response.data.vouchers_added} vouchers`);
      if (response.data.errors.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      fetchVouchers();
      fetchCertTypes();
    } catch (error) {
      toast.error('Failed to upload vouchers');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddVoucher = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/vouchers`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Voucher created successfully');
      setShowAddDialog(false);
      setFormData({ code: '', certification_type: '' });
      fetchVouchers();
      fetchCertTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create voucher');
    }
  };

  const handleEditVoucher = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/vouchers/${selectedVoucher.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Voucher updated successfully');
      setShowEditDialog(false);
      setSelectedVoucher(null);
      fetchVouchers();
    } catch (error) {
      toast.error('Failed to update voucher');
    }
  };

  const handleArchive = async (voucherId, archived) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/vouchers/${voucherId}`, { archived }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Voucher ${archived ? 'archived' : 'unarchived'} successfully`);
      fetchVouchers();
    } catch (error) {
      toast.error('Failed to update voucher');
    }
  };

  const openEditDialog = (voucher) => {
    setSelectedVoucher(voucher);
    setFormData({
      code: voucher.code,
      certification_type: voucher.certification_type
    });
    setShowEditDialog(true);
  };

  const filteredVouchers = vouchers.filter(v => {
    if (filter === 'assigned') return v.is_assigned;
    if (filter === 'available') return !v.is_assigned;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="voucher-management">
      <div>
        <h2 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Voucher Management
        </h2>
        <p className="text-slate-600 dark:text-slate-400">Upload and manage certification voucher codes by type</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 dark:border-slate-700" >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Upload
            </CardTitle>
            <CardDescription>
              Upload an Excel file with voucher codes. Format: Column A = Voucher Code, Column B = Certification Type
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                data-testid="voucher-upload-input"
                className="max-w-md"
              />
              {uploading && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">Uploading...</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-700" >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Manual Add
            </CardTitle>
            <CardDescription>
              Add a single voucher manually
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-400" data-testid="add-voucher-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Voucher
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-voucher-dialog">
                <DialogHeader>
                  <DialogTitle>Add New Voucher</DialogTitle>
                  <DialogDescription>Enter voucher details</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddVoucher} className="space-y-4">
                  <div>
                    <Label htmlFor="code">Voucher Code</Label>
                    <Input
                      id="code"
                      data-testid="voucher-code-input"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      required
                      placeholder="e.g., CERT-12345"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cert_type">Certification Type</Label>
                    <Input
                      id="cert_type"
                      data-testid="voucher-cert-type-input"
                      value={formData.certification_type}
                      onChange={(e) => setFormData({ ...formData, certification_type: e.target.value })}
                      required
                      placeholder="e.g., Cisco CCNA"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-voucher-button">
                    Add Voucher
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 dark:border-slate-700" >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Voucher List
              </CardTitle>
              <CardDescription>{filteredVouchers.length} vouchers</CardDescription>
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="show-archived"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                  data-testid="show-archived-checkbox"
                />
                <label htmlFor="show-archived" className="text-sm cursor-pointer">
                  Show Archived
                </label>
              </div>
              <Select value={certTypeFilter} onValueChange={setCertTypeFilter}>
                <SelectTrigger className="w-48" data-testid="cert-type-filter">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {certTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Tabs value={filter} onValueChange={setFilter} className="w-auto">
                <TabsList>
                  <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                  <TabsTrigger value="available" data-testid="filter-available">Available</TabsTrigger>
                  <TabsTrigger value="assigned" data-testid="filter-assigned">Assigned</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800" >
                    <TableHead>Voucher Code</TableHead>
                    <TableHead>Certification Type</TableHead>
                    <TableHead>Upload Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVouchers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 h-32">
                        No vouchers found. Upload an Excel file or add manually to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredVouchers.map((voucher) => (
                      <TableRow key={voucher.id} data-testid={`voucher-row-${voucher.code}`}>
                        <TableCell className="font-mono font-medium">{voucher.code}</TableCell>
                        <TableCell>
                          <span className="inline-flex px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                            {voucher.certification_type}
                          </span>
                        </TableCell>
                        <TableCell>{new Date(voucher.uploaded_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {voucher.archived ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                              Archived
                            </span>
                          ) : voucher.is_assigned ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                              <CheckCircle className="w-3 h-3" />
                              Assigned
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                              <XCircle className="w-3 h-3" />
                              Available
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600 dark:text-slate-400">
                          {voucher.assigned_to || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {!voucher.is_assigned && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDialog(voucher)}
                                data-testid={`edit-voucher-${voucher.id}`}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchive(voucher.id, !voucher.archived)}
                              data-testid={`archive-voucher-${voucher.id}`}
                            >
                              <Archive className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Voucher</DialogTitle>
            <DialogDescription>Update voucher information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditVoucher} className="space-y-4">
            <div>
              <Label htmlFor="edit-code">Voucher Code</Label>
              <Input
                id="edit-code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-cert">Certification Type</Label>
              <Input
                id="edit-cert"
                value={formData.certification_type}
                onChange={(e) => setFormData({ ...formData, certification_type: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Update Voucher
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoucherManagement;